import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { QuestionType } from '../../../../../../annotation/question.model';
import { AnnotationToolFilterType, EmptyAnnotationToolFilter, IAnnotationToolFilter, IImageFiltersData } from '../../../imageFilters.model';
import { AnnotationToolRowView } from './AnnotationToolRowView';

interface IProps {
  availableImageFilters: IImageFiltersData;
  toolFilter: IAnnotationToolFilter;
  showDelete: boolean;
  onDelete(order: number): void;
  onToolFilterChanged(toolFilter: IAnnotationToolFilter): void;
}

export const AnnotationToolRowForm = observer((props: IProps): ReactElement => {
  const handleL1FilterChanged = (id: string | null): void => {
    if (props.toolFilter.l1Filter === id) {
      return;
    }

    if (id === null) {
      props.onToolFilterChanged({ ...EmptyAnnotationToolFilter, order: props.toolFilter.order });
      return;
    }

    const { annotationTypes, questionFilters } = props.availableImageFilters;

    const annotationType = annotationTypes.find((x) => x.id === id);
    if (annotationType) {
      props.onToolFilterChanged({
        ...EmptyAnnotationToolFilter,
        order: props.toolFilter.order,
        l1Filter: id,
        l1FilterType: AnnotationToolFilterType.AnnotationTool,
      });
    } else {
      const question = questionFilters.find((x) => x.id === id);
      if (question) {
        let type: AnnotationToolFilterType | undefined = undefined;
        switch (question.answerType) {
          case QuestionType.OPEN:
            type = AnnotationToolFilterType.QuestionAnswerText;
            break;
          case QuestionType.NUMBER:
            type = AnnotationToolFilterType.QuestionAnswerNumber;
            break;
          case QuestionType.TRUE_FALSE:
          case QuestionType.ALTERNATIVE:
          case QuestionType.MULTISELECT:
          case QuestionType.STATUS_GOOD_BAD:
          case QuestionType.SET_TRAIN_TEST:
          default:
            type = AnnotationToolFilterType.QuestionAnswer;
            break;
        }
        props.onToolFilterChanged({
          ...EmptyAnnotationToolFilter,
          order: props.toolFilter.order,
          l1Filter: id,
          l1FilterType: AnnotationToolFilterType.Question,
          l3FilterType: type,
        });
      }
    }
  };

  const handleL2FilterChanged = (id: string | null): void => {
    if (props.toolFilter.l2Filter === id) {
      return;
    }

    if (id === null) {
      props.onToolFilterChanged({
        ...EmptyAnnotationToolFilter,
        order: props.toolFilter.order,
        l1FilterType: props.toolFilter.l1FilterType,
        l1Filter: props.toolFilter.l1Filter,
      });
      return;
    }

    const { annotationTypes } = props.availableImageFilters;
    const annotationType = annotationTypes.find((x) => x.id === props.toolFilter.l1Filter);
    if (annotationType) {
      const attribute = annotationType.attributes.find((x) => x.id === id);
      if (attribute) {
        let l3FilterType: AnnotationToolFilterType | undefined = undefined;
        switch (attribute.answerType) {
          case QuestionType.OPEN:
            l3FilterType = AnnotationToolFilterType.AttributeAnswerText;
            break;
          case QuestionType.NUMBER:
            l3FilterType = AnnotationToolFilterType.AttributeAnswerNumber;
            break;
          case QuestionType.TRUE_FALSE:
          case QuestionType.ALTERNATIVE:
          case QuestionType.MULTISELECT:
          case QuestionType.STATUS_GOOD_BAD:
          case QuestionType.SET_TRAIN_TEST:
          default:
            l3FilterType = AnnotationToolFilterType.AttributeAnswer;
            break;
        }
        props.onToolFilterChanged({
          ...props.toolFilter,
          l3FilterType,
          l2Filter: id,
          l2FilterType: AnnotationToolFilterType.Attribute,
          l3Filters: [],
        });
        return;
      }
    }
  };

  const handleL3MultiselectFilterChanged = (ids: string[]): void => {
    const { annotationTypes, questionFilters } = props.availableImageFilters;
    const annotationType = annotationTypes.find((x) => x.id === props.toolFilter.l1Filter);
    if (annotationType) {
      const attribute = annotationType.attributes.find((x) => x.id === props.toolFilter.l2Filter);
      if (attribute) {
        const answers = attribute.possibleAnswers.filter((x) => ids.some((y) => y === x.id)).map((x) => x.id);
        props.onToolFilterChanged({ ...props.toolFilter, l3Filters: answers });
      }
    } else {
      const question = questionFilters.find((x) => x.id === props.toolFilter.l1Filter);
      if (question) {
        const answers = question.possibleAnswers.filter((x) => ids.some((y) => y === x.id)).map((x) => x.id);
        props.onToolFilterChanged({ ...props.toolFilter, l3Filters: answers });
      }
    }
  };

  const handleL3SelectFilterChanged = (id: string | null): void => {
    if (!id) {
      props.onToolFilterChanged({
        ...props.toolFilter,
        l3Filters: [],
      });
      return;
    }

    const { annotationTypes, questionFilters } = props.availableImageFilters;
    const annotationType = annotationTypes.find((x) => x.id === props.toolFilter.l1Filter);
    if (annotationType) {
      const attribute = annotationType.attributes.find((x) => x.id === props.toolFilter.l2Filter);
      if (attribute) {
        const answer = attribute.possibleAnswers.find((x) => x.id === id);
        if (answer) {
          props.onToolFilterChanged({ ...props.toolFilter, l3Filters: [id] });
          return;
        }
      }
    } else {
      const question = questionFilters.find((x) => x.id === props.toolFilter.l1Filter);
      if (question) {
        const answer = question.possibleAnswers.find((x) => x.id === id);
        if (answer) {
          props.onToolFilterChanged({ ...props.toolFilter, l3Filters: [id] });
          return;
        }
      }
    }
  };

  const handleL3TextFilterChanged = (text: string): void => {
    props.onToolFilterChanged({ ...props.toolFilter, l3Filters: [text] });
  };

  const handleL3MinNumberFilterChanged = (value: number | string): void => {
    if (props.toolFilter.l3Filters.length !== 2) {
      props.onToolFilterChanged({ ...props.toolFilter, l3Filters: ['', ''] });
    }
    props.onToolFilterChanged({ ...props.toolFilter, l3Filters: [value.toString() || '', props.toolFilter.l3Filters[1]] });
  };

  const handleL3MaxNumberFilterChanged = (value: string): void => {
    if (props.toolFilter.l3Filters.length !== 2) {
      props.onToolFilterChanged({ ...props.toolFilter, l3Filters: ['', ''] });
    }
    props.onToolFilterChanged({ ...props.toolFilter, l3Filters: [props.toolFilter.l3Filters[0], value || ''] });
  };

  const handleDelete = (): void => {
    props.onDelete(props.toolFilter.order);
  };

  return (
    <AnnotationToolRowView
      availableImageFilters={props.availableImageFilters}
      toolFilter={props.toolFilter}
      showDelete={props.showDelete}
      onDelete={handleDelete}
      onL1FilterChanged={handleL1FilterChanged}
      onL2FilterChanged={handleL2FilterChanged}
      onL3MultiselectFilterChanged={handleL3MultiselectFilterChanged}
      onL3MaxNumberFilterChanged={handleL3MaxNumberFilterChanged}
      onL3MinNumberFilterChanged={handleL3MinNumberFilterChanged}
      onL3SelectFilterChanged={handleL3SelectFilterChanged}
      onL3TextFilterChanged={handleL3TextFilterChanged}
    />
  );
});
